import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import ForgotPassword from './components/ForgotPassword';
import Profile from './components/Profile';
import Settings from './components/Settings';
import UserManagement from './components/UserManagement';
import AcceptInvite from './components/AcceptInvite';
import SpAccountList from './components/SpAccountList';
import AmazonReports from './components/AmazonReports';

type Page = 'profile' | 'settings' | 'user-management' | 'sp-accounts' | 'amazon-reports';
type AuthPage = 'login' | 'signup' | 'forgot-password';

const NAV_ICONS: Record<Page, React.ReactNode> = {
  'sp-accounts': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  'amazon-reports': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  'user-management': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  profile: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  settings: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

const PAGE_TITLES: Record<Page, string> = {
  'user-management': 'User Management',
  profile: 'Profile',
  settings: 'Settings',
  'sp-accounts': 'Amazon Accounts',
  'amazon-reports': 'Amazon Data',
};

function getInviteTokenFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/invite\/([A-Za-z0-9_-]+)/);
  return match ? match[1] : null;
}

function getResetTokenFromUrl(): string | null {
  if (window.location.pathname !== '/reset-password') return null;
  return new URLSearchParams(window.location.search).get('token');
}

const PAGE_URLS: Record<Page, string> = {
  'user-management': '/user-management',
  profile: '/profile',
  settings: '/settings',
  'sp-accounts': '/amazon-accounts',
  'amazon-reports': '/amazon-reports',
};

const URL_TO_PAGE: Record<string, Page> = {
  '/': 'amazon-reports',
  '/amazon-accounts': 'sp-accounts',
  '/amazon-reports': 'amazon-reports',
  '/user-management': 'user-management',
  '/profile': 'profile',
  '/settings': 'settings',
};

function parseUrlState(): Page {
  const path = window.location.pathname;
  return URL_TO_PAGE[path] ?? 'amazon-reports';
}

function App() {
  const { user, loading: authLoading, logout, activeOrg, setActiveOrg, refreshUser, hasPermission } = useAuth();
  const [resetToken] = useState<string | null>(getResetTokenFromUrl);
  const [authPage, setAuthPage] = useState<AuthPage>(resetToken ? 'forgot-password' : 'login');
  const [currentPage, setCurrentPage] = useState<Page>(parseUrlState);
  const [inviteToken] = useState<string | null>(getInviteTokenFromUrl);
  const { theme, toggleTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Which pages the current user may open. Admins hold every permission via
  // hasPermission, so they always pass. Amazon Data is visible if the user can
  // view at least one of its tabs.
  const canView = useCallback((page: Page): boolean => {
    switch (page) {
      case 'sp-accounts': return hasPermission('accounts.view');
      case 'amazon-reports':
        return ['orders.view', 'inventory.view', 'business_reports.view', 'scheduler.view'].some(hasPermission);
      case 'user-management': return !!user?.is_admin;
      case 'settings': return hasPermission('settings.view');
      case 'profile': return true;
      default: return true;
    }
  }, [hasPermission, user]);

  // First page a user can land on after login (Profile is always accessible).
  const firstAccessiblePage: Page =
    (['amazon-reports', 'sp-accounts', 'settings', 'profile'] as Page[]).find(canView) ?? 'profile';

  // Redirect to the first accessible page on fresh login (user null → set)
  const prevUserRef = React.useRef<typeof user>(undefined);
  useEffect(() => {
    const wasLoggedOut = prevUserRef.current === null;
    prevUserRef.current = user;
    if (user && wasLoggedOut) {
      setCurrentPage(firstAccessiblePage);
      window.history.replaceState({}, '', PAGE_URLS[firstAccessiblePage]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setCurrentPage(parseUrlState());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNavClick = useCallback((page: Page) => {
    setCurrentPage(page);
    window.history.pushState({}, '', PAGE_URLS[page]);
    setMobileOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const getUserInitials = (): string => {
    if (user?.full_name) {
      const parts = user.full_name.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return parts[0][0].toUpperCase();
    }
    return '?';
  };

  if (authLoading) {
    return (
      <div className="auth-page">
        <div className="loading">
          <div className="spinner"></div>
          Loading...
        </div>
      </div>
    );
  }

  if (inviteToken) {
    return (
      <AcceptInvite
        token={inviteToken}
        onAccepted={async () => {
          await refreshUser();
          window.history.replaceState(null, '', '/');
          window.location.replace('/');
        }}
      />
    );
  }

  if (!user) {
    if (authPage === 'signup') return <Signup onSwitchToLogin={() => setAuthPage('login')} />;
    if (authPage === 'forgot-password') return <ForgotPassword onSwitchToLogin={() => { window.history.replaceState(null, '', '/'); setAuthPage('login'); }} initialToken={resetToken ?? undefined} />;
    return (
      <Login
        onSwitchToSignup={() => setAuthPage('signup')}
        onSwitchToForgot={() => setAuthPage('forgot-password')}
      />
    );
  }

  const renderPage = () => {
    if (!canView(currentPage)) {
      return (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>No access</div>
          <div style={{ color: 'var(--text-muted)' }}>
            You don't have permission to view this section.
          </div>
        </div>
      );
    }
    switch (currentPage) {
      case 'user-management': return <UserManagement />;
      case 'profile': return <Profile />;
      case 'settings': return <Settings />;
      case 'sp-accounts': return <SpAccountList />;
      case 'amazon-reports': return <AmazonReports />;
      default: return <SpAccountList />;
    }
  };

  const amazonNavItems: { page: Page; label: string }[] = [
    ...(canView('sp-accounts') ? [{ page: 'sp-accounts' as Page, label: 'Amazon Accounts' }] : []),
    ...(canView('amazon-reports') ? [{ page: 'amazon-reports' as Page, label: 'Amazon Data' }] : []),
  ];

  const adminNavItems: { page: Page; label: string }[] = [
    ...(user?.is_admin ? [{ page: 'user-management' as Page, label: 'User Management' }] : []),
  ];

  const accountNavItems: { page: Page; label: string }[] = [
    { page: 'profile', label: 'Profile' },
    ...(canView('settings') ? [{ page: 'settings' as Page, label: 'Settings' }] : []),
  ];

  const sidebarContent = (
    <>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="url(#logo-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <defs>
              <linearGradient id="logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#7c6aef" />
                <stop offset="100%" stopColor="#9588f5" />
              </linearGradient>
            </defs>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="url(#logo-grad)" />
            <line x1="12" y1="22.08" x2="12" y2="12" stroke="url(#logo-grad)" />
          </svg>
          <div className="sidebar-logo-text">
            <h1>Report Tracker</h1>
            {activeOrg && !sidebarCollapsed && (
              <div className="subtitle" style={{ fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                {activeOrg.org_name}
              </div>
            )}
          </div>
        </div>
      </div>

      {user.organizations && user.organizations.length > 1 && !sidebarCollapsed && (
        <div style={{ padding: '0 12px 8px' }}>
          <select
            className="form-input"
            style={{ fontSize: 12, padding: '4px 8px' }}
            value={activeOrg?.org_id || ''}
            onChange={(e) => {
              const org = user.organizations.find((o) => o.org_id === e.target.value);
              if (org) setActiveOrg(org);
            }}
          >
            {user.organizations.map((org) => (
              <option key={org.org_id} value={org.org_id}>{org.org_name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="nav-section-label">Amazon SP-API</div>
      <ul className="nav-links">
        {amazonNavItems.map((item) => (
          <li
            key={item.page}
            className={currentPage === item.page ? 'active' : ''}
            onClick={() => handleNavClick(item.page)}
            title={sidebarCollapsed ? item.label : undefined}
          >
            <span className="nav-icon">{NAV_ICONS[item.page]}</span>
            <span className="nav-label">{item.label}</span>
          </li>
        ))}
      </ul>

      {adminNavItems.length > 0 && (
        <>
          <div className="nav-section-label">Admin</div>
          <ul className="nav-links">
            {adminNavItems.map((item) => (
              <li
                key={item.page}
                className={currentPage === item.page ? 'active' : ''}
                onClick={() => handleNavClick(item.page)}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <span className="nav-icon">{NAV_ICONS[item.page]}</span>
                <span className="nav-label">{item.label}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="nav-section-label">Account</div>
      <ul className="nav-links nav-links-bottom">
        {accountNavItems.map((item) => (
          <li
            key={item.page}
            className={currentPage === item.page ? 'active' : ''}
            onClick={() => handleNavClick(item.page)}
            title={sidebarCollapsed ? item.label : undefined}
          >
            <span className="nav-icon">{NAV_ICONS[item.page]}</span>
            <span className="nav-label">{item.label}</span>
          </li>
        ))}
        <li onClick={handleLogout} title={sidebarCollapsed ? 'Sign Out' : undefined}>
          <span className="nav-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </span>
          <span className="nav-label">Sign Out</span>
        </li>
      </ul>
      <div className="sidebar-footer">
        <button className="theme-toggle" onClick={toggleTheme} title={sidebarCollapsed ? (theme === 'dark' ? 'Light Mode' : 'Dark Mode') : undefined}>
          <span className="theme-toggle-icon">
            {theme === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </span>
          <span className="nav-label">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
        </button>
        <button
          className="sidebar-collapse-btn"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            {sidebarCollapsed ? (
              <>
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </>
            ) : (
              <>
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </>
            )}
          </svg>
        </button>
      </div>
    </>
  );

  return (
    <div className={`app ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}

      <nav className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        {sidebarContent}
      </nav>

      <header className="topbar">
        <div className="topbar-left">
          <button className="topbar-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {mobileOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>
          <div className="topbar-breadcrumb">
            <span className="topbar-page-icon">{NAV_ICONS[currentPage]}</span>
            <h3 className="topbar-title">{PAGE_TITLES[currentPage]}</h3>
          </div>
        </div>
        <div className="topbar-right">
          {user && (
            <div style={{ fontSize: 12, padding: '3px 10px', borderRadius: 12, background: 'var(--bg-secondary)', color: 'var(--text-muted)', marginRight: 4, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.is_admin ? 'Admin' : (user.role_name || 'No role')}
              {activeOrg ? ` · ${activeOrg.org_name}` : ''}
            </div>
          )}
          {canView('settings') && (
            <button className="topbar-icon-btn" onClick={() => handleNavClick('settings')} title="Settings">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          )}
          <button className="topbar-icon-btn" onClick={toggleTheme} title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}>
            {theme === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
          <button className="topbar-icon-btn" onClick={handleLogout} title="Sign Out">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
          <button
            className="topbar-avatar"
            onClick={() => handleNavClick('profile')}
            title="Profile"
          >
            {getUserInitials()}
          </button>
        </div>
      </header>

      <main className="main-content">
        {renderPage()}
      </main>
    </div>
  );
}

export default App;
